import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { Reveal } from "@/components/Reveal";
import { SERVICE_GROUPS, PROSES_KERJA } from "@/data/company";

export default function Services() {
  return (
    <>
      <SEOHead
        title="Layanan Konstruksi, Arsitektur, Interior & Perdagangan | PT. Luminara Global Persada"
        description="Layanan PT. Luminara Global Persada: konstruksi gedung, konstruksi sipil & infrastruktur, finishing & interior, perdagangan besar, serta arsitektur, desain interior & lanskap."
      />

      <section className="pt-40 pb-20 bg-navy-900 border-b border-white/10" data-testid="services-header">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Layanan Kami</p>
            <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight max-w-3xl">
              Layanan PT. Luminara Global Persada
            </h1>
            <p className="mt-6 max-w-2xl text-slate-400 leading-relaxed">
              Lima kategori layanan yang saling terhubung — dari perencanaan desain, pelaksanaan
              konstruksi, hingga pengadaan — dikelola dalam satu standar mutu.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad" data-testid="services-list-section">
        <div className="container-site grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <p className="kicker mb-4">Kategori Layanan</p>
                <h2 className="font-heading font-semibold text-2xl md:text-3xl tracking-tight">
                  Pilih kategori kebutuhan Anda
                </h2>
                <nav className="mt-8 flex flex-col gap-1" aria-label="Navigasi kategori layanan">
                  {SERVICE_GROUPS.map((g, i) => (
                    <a
                      key={g.id}
                      href={`#${g.id}`}
                      data-testid={`service-nav-${g.id}`}
                      className="group flex items-center gap-4 py-3 border-b border-white/10 text-slate-300 hover:text-gold transition-colors duration-300"
                    >
                      <span className="font-heading text-sm text-gold/60 group-hover:text-gold">0{i + 1}</span>
                      <span className="text-sm tracking-wide">{g.title}</span>
                      <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </a>
                  ))}
                </nav>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-24">
            {SERVICE_GROUPS.map((g) => (
              <section key={g.id} id={g.id} data-testid={`service-section-${g.id}`} className="scroll-mt-28">
                <Reveal>
                  <p className="text-xs tracking-[0.25em] uppercase text-slate-500">{g.en}</p>
                  <h2 className="mt-2 font-heading font-semibold text-2xl md:text-3xl tracking-tight text-white">
                    {g.title}
                  </h2>
                  <p className="mt-4 text-slate-400 leading-relaxed max-w-2xl">{g.desc}</p>
                </Reveal>
                <div className="mt-10 grid sm:grid-cols-2 gap-6">
                  {g.items.map((it, i) => (
                    <Reveal key={it.kbli} delay={i * 0.06}>
                      <div
                        data-testid={`service-item-${it.kbli}`}
                        className="h-full bg-navy-900 border border-white/10 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50"
                      >
                        <span className="inline-block text-[11px] font-bold tracking-widest text-gold border border-gold/40 px-2.5 py-1">
                          KBLI {it.kbli}
                        </span>
                        <h3 className="mt-4 font-heading font-semibold text-lg text-white">{it.name}</h3>
                        <p className="mt-3 text-sm text-slate-400 leading-relaxed">{it.desc}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-navy-900 border-y border-white/10" data-testid="process-section">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Proses Kerja</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Enam tahap kerja yang terukur dan transparan
            </h2>
          </Reveal>
          <div className="mt-16 relative max-w-3xl">
            <div className="absolute left-[22px] md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-gold/60 via-gold/25 to-transparent md:-translate-x-1/2" aria-hidden="true" />
            <div className="space-y-14">
              {PROSES_KERJA.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div
                    data-testid={`process-step-${i}`}
                    className={`relative flex gap-8 md:w-1/2 ${
                      i % 2 === 0
                        ? "md:pr-14 md:text-right md:flex-row-reverse"
                        : "md:ml-auto md:pl-14"
                    }`}
                  >
                    <div className="relative shrink-0 md:absolute md:top-0 md:left-auto md:right-auto flex md:block">
                      <span
                        className={`w-11 h-11 border border-gold bg-navy-950 flex items-center justify-center font-heading font-semibold text-gold z-10 relative ${
                          i % 2 === 0 ? "md:absolute md:-right-[22px]" : "md:absolute md:-left-[22px]"
                        }`}
                      >
                        {i + 1}
                      </span>
                    </div>
                    <div className={`${i % 2 === 0 ? "md:w-full" : "md:w-full md:ml-11"} ml-0 md:ml-0`}>
                      <h3 className="font-heading font-semibold text-xl text-white flex items-center gap-3 md:justify-inherit">
                        <ClipboardCheck size={18} className="text-gold md:hidden" />
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="services-cta-section">
        <div className="container-site">
          <Reveal>
            <div className="border border-gold/30 bg-navy-800 px-8 py-14 md:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="max-w-xl">
                <h2 className="font-heading font-semibold text-2xl md:text-3xl tracking-tight">
                  Butuh penawaran untuk proyek Anda?
                </h2>
                <p className="mt-4 text-slate-400 leading-relaxed">
                  Sampaikan kebutuhan Anda — tim kami akan menyiapkan kajian awal dan penawaran yang transparan.
                </p>
              </div>
              <Link to="/kontak" data-testid="services-cta-contact" className="btn-gold shrink-0">
                Ajukan Kebutuhan <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
